import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { PAGE_SEO } from './seo-pages';

/**
 * Métadonnées SEO d'une page, déclarées via `data: { seo: {...} }` sur la route.
 */
export interface SeoData {
  /** Balise <title>. ≤ 60 caractères, mot-clé cible en tête. */
  title: string;
  /** <meta name="description">. ≤ 155 caractères. */
  description: string;
  /** Chemin canonique sans domaine (ex. '/assurance-credit-emprunteur'). Défaut : l'URL courante. */
  canonicalPath?: string;
  /** Image de partage social, absolue ou relative au site. */
  ogImage?: string;
  /** 'website' (défaut) ou 'article'. */
  ogType?: 'website' | 'article';
  /** Exclut la page de l'index (espace connecté, pages techniques). */
  noindex?: boolean;
}

export const SITE_URL = 'https://brokins.fr';
const SITE_NAME = 'Brokins';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/og-default.jpg`;
const TITLE_SUFFIX = ' | Brokins';

/**
 * Applique les balises <title>, <meta> et <link rel="canonical"> à chaque navigation.
 *
 * Limite assumée : le site est rendu côté client. Ce service corrige le partage social
 * (Facebook, LinkedIn, X) et les crawlers qui exécutent JavaScript, mais un crawler
 * sans JS — GPTBot, ClaudeBot, PerplexityBot — ne voit toujours rien. Seul le rendu
 * statique (blog généré, prerender des pages commerciales) résout ce point.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  apply(seo: SeoData | undefined, currentPath: string): void {
    // `data: { seo }` sur la route l'emporte, sinon on cherche dans la carte des pages.
    const resolved = seo ?? PAGE_SEO[currentPath] ?? PAGE_SEO[currentPath.replace(/[/]$/, '')];

    if (!resolved) {
      // Écrans applicatifs (espace connecté) : on les sort de l'index plutôt que de
      // laisser fuiter les métadonnées de la page précédente.
      this.applyFallback(currentPath);
      return;
    }

    const fullTitle = resolved.title.includes('Brokins') ? resolved.title : resolved.title + TITLE_SUFFIX;
    const canonical = SITE_URL + (resolved.canonicalPath ?? currentPath);
    const image = this.absolute(resolved.ogImage ?? DEFAULT_OG_IMAGE);

    this.title.setTitle(fullTitle);
    this.meta.updateTag({ name: 'description', content: resolved.description });

    if (resolved.noindex) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    } else {
      this.meta.updateTag({
        name: 'robots',
        content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      });
    }

    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ property: 'og:description', content: resolved.description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:type', content: resolved.ogType ?? 'website' });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:locale', content: 'fr_FR' });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:description', content: resolved.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonical(canonical);
  }

  /**
   * Pages sans SEO déclaré (espace connecté). On les sort de l'index plutôt que de
   * laisser Google découvrir des écrans applicatifs vides.
   */
  private applyFallback(currentPath: string): void {
    this.title.setTitle(SITE_NAME);
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    this.setCanonical(SITE_URL + currentPath);
  }

  private absolute(url: string): string {
    return url.startsWith('http') ? url : SITE_URL + (url.startsWith('/') ? url : '/' + url);
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
